// plan-04 S04 + plan-10 S03: public widget interface
// Refactored: exports StageWidget; divine command activates stage + streams to terminal

import type { CommandCtx } from '../../oracle/commands'
import type { StageWidget, StageHandle, StageCtx } from '../stage/types'
import { generate } from './generate'
import { renderDivine } from './render'
import { play } from '../../audio/sfx'
import { load } from '../../core/save'
import type { GeneratedPost } from './generate'

let invocationCount = 0

// --- StageWidget implementation ---

export const divineWidget: StageWidget = {
  id: 'divine',
  label: 'DIVINATION // VAGUEPOST GENERATOR',
  mount(host: HTMLElement, ctx: StageCtx): StageHandle {
    const wrapper = document.createElement('div')
    wrapper.className = 'vt-divine-stage'
    wrapper.style.cssText = 'font-family: var(--vt-font-mono); font-size: var(--vt-fs-body); line-height: var(--vt-line-height);'

    // show latest divine post or prompt
    const s = load()
    const lastHash = s.divineSeen.length > 0 ? s.divineSeen[s.divineSeen.length - 1] : null

    if (lastHash) {
      const post = generate({ fromHash: lastHash })
      if (post) {
        renderPostCard(wrapper, post)
      }
    } else {
      const prompt = document.createElement('div')
      prompt.style.color = 'var(--vt-green-dim)'
      prompt.style.fontStyle = 'italic'
      prompt.textContent = '// type divine to generate a vaguepost.'
      wrapper.appendChild(prompt)
    }

    // history list
    if (s.divineSeen.length > 0) {
      const histLabel = document.createElement('div')
      histLabel.style.cssText = 'margin-top: 12px; font-size: clamp(9px, 0.85vw, 11px); color: var(--vt-green-dim);'
      histLabel.textContent = 'divination history:'
      wrapper.appendChild(histLabel)

      const histList = document.createElement('div')
      histList.style.cssText = 'margin-top: 4px; display: flex; flex-direction: column; gap: 2px;'
      for (const hash of [...s.divineSeen].reverse().slice(0, 12)) {
        const item = document.createElement('div')
        item.style.cssText = 'font-size: clamp(10px, 0.9vw, 12px); color: var(--vt-green-dim); cursor: pointer; padding: 2px 0;'
        item.textContent = `◇ ${hash}`
        item.addEventListener('click', () => {
          ctx.emitOracle(`> divine ${hash}`)
          const input = document.querySelector('input[aria-label="oracle command input"]') as HTMLInputElement | null
          const form = input?.closest('form')
          if (input && form) {
            input.value = `divine ${hash}`
            form.dispatchEvent(new Event('submit', { cancelable: true }))
          }
        })
        histList.appendChild(item)
      }
      wrapper.appendChild(histList)
    }

    host.appendChild(wrapper)

    return {
      dismiss() {
        // no persistent cleanup needed
      },
      onSwap() {
        wrapper.innerHTML = ''
        const s = load()
        const lastHash = s.divineSeen.length > 0 ? s.divineSeen[s.divineSeen.length - 1] : null
        if (lastHash) {
          const post = generate({ fromHash: lastHash })
          if (post) renderPostCard(wrapper, post)
        } else {
          const prompt = document.createElement('div')
          prompt.style.color = 'var(--vt-green-dim)'
          prompt.style.fontStyle = 'italic'
          prompt.textContent = '// type divine to generate a vaguepost.'
          wrapper.appendChild(prompt)
        }
        if (s.divineSeen.length > 0) {
          const histLabel = document.createElement('div')
          histLabel.style.cssText = 'margin-top: 12px; font-size: clamp(9px, 0.85vw, 11px); color: var(--vt-green-dim);'
          histLabel.textContent = 'divination history:'
          wrapper.appendChild(histLabel)

          const histList = document.createElement('div')
          histList.style.cssText = 'margin-top: 4px; display: flex; flex-direction: column; gap: 2px;'
          for (const hash of [...s.divineSeen].reverse().slice(0, 12)) {
            const item = document.createElement('div')
            item.style.cssText = 'font-size: clamp(10px, 0.9vw, 12px); color: var(--vt-green-dim); cursor: pointer; padding: 2px 0;'
            item.textContent = `◇ ${hash}`
            item.addEventListener('click', () => {
              ctx.emitOracle(`> divine ${hash}`)
              const input = document.querySelector('input[aria-label="oracle command input"]') as HTMLInputElement | null
              const form = input?.closest('form')
              if (input && form) {
                input.value = `divine ${hash}`
                form.dispatchEvent(new Event('submit', { cancelable: true }))
              }
            })
            histList.appendChild(item)
          }
          wrapper.appendChild(histList)
        }
      },
    }
  },
}

function renderPostCard(parent: HTMLElement, post: GeneratedPost): void {
  const card = document.createElement('div')
  card.style.cssText = 'padding: 8px; border: 1px solid var(--vt-outline); background: rgba(0,0,0,0.3);'

  const header = document.createElement('div')
  header.style.cssText = 'color: var(--vt-green-dim); font-size: clamp(9px, 0.85vw, 11px); margin-bottom: 4px;'
  header.textContent = '@network'
  card.appendChild(header)

  const body = document.createElement('div')
  body.style.color = 'var(--vt-term-green)'
  body.textContent = `> ${post.sentence}`
  card.appendChild(body)

  const probably = document.createElement('div')
  probably.style.cssText = 'color: var(--vt-green-dim); font-size: clamp(10px, 0.9vw, 12px);'
  probably.textContent = `> ${post.probablyTag}`
  card.appendChild(probably)

  const meta = document.createElement('div')
  meta.style.cssText = 'margin-top: 4px; font-size: clamp(9px, 0.85vw, 11px); color: var(--vt-green-dim);'
  meta.textContent = `@quorum: ${post.quorumTag}. @certainty: ${post.certainty}. @hash: ${post.hash}`
  card.appendChild(meta)

  parent.appendChild(card)
}

// --- Oracle command handler (streams post into terminal + activates stage) ---

export async function handleDivine(ctx: CommandCtx, args: string[]): Promise<void> {
  const hash = args[0]
  let post: ReturnType<typeof generate> = null

  if (hash) {
    post = generate({ fromHash: hash })
    if (!post) {
      const errLine = document.createElement('div')
      errLine.className = 'vt-line-oracle'
      errLine.style.color = 'var(--vt-green-dim)'
      errLine.textContent = '> hash unresolved. consensus: probably.'
      ctx.history.appendChild(errLine)
      play('command_reject')
      return
    }
    await renderDivine(ctx, post)
  } else {
    invocationCount++
    post = generate({ sessionN: invocationCount })
    if (post) {
      await renderDivine(ctx, post)
    }
  }

  // activate stage with divine widget
  const { getStage } = await import('../stage/stage')
  const stage = getStage()
  if (stage) {
    stage.activate('divine', hash ? [hash] : [])
  }
}
